import { useCallback, useEffect, useMemo, useState } from 'react'
import RightPanel from 'components/RightPanel/RightPanel'
import VendorList from 'features/Vendors/components/VendorList'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Columns3, Handshake, LayoutTemplate, Presentation as PresentationIcon, X } from 'lucide-react'

import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import { Alert, AlertDescription } from 'components/ui/alert'
import { Button } from 'components/ui/button'
import { TablePagination } from 'components/ui/pagination'
import { cn } from 'lib/utils'
import routes from 'constants/routes'
import { toast } from 'components/ui/toast-store'
import {
	clearError,
	deleteReport as deleteReportThunk,
	fetchColumns,
	fetchGrains,
	fetchReports,
	saveReport as saveReportThunk,
	saveReportAsTemplate as saveReportAsTemplateThunk,
	updateReport as updateReportThunk,
} from 'slices/explorerSlice'

import { AddToPresentationDialog } from 'features/Presentations/components/AddToPresentationDialog'
import { BuildStoriesDialog, type StoryGrain } from 'features/Presentations/components/BuildStoriesDialog'
import { toStoredFilter } from 'features/Presentations/components/inspector/ExplorerTableInspector'
import { isPresentationsEnabled } from 'features/Presentations/featureFlag'

import { ColumnPickerSheet } from './ColumnPickerSheet'
import { GRAIN_LABELS, HOME_TEMPLATE_SCOPE_BY_GRAIN, grainForSlug, slugForGrain, templateDraftPath } from '../constants'
import { ExplorerTemplateGallery } from './ExplorerTemplateGallery'
import { ScopeChip } from './ScopeChip'
import { ExplorerScopeContext } from '../hooks/useExplorerScope'
import { SaveReportDialog, type SaveReportMode } from './SaveReportDialog'
import { SavedReportPicker } from './SavedReportPicker'
import { ExportMenu } from './ExportMenu'
import { FilterBar } from './FilterBar'
import { AskFilterInput, type AskFilterLayout } from './AskFilterInput'
import { isAskMdtAvailable } from 'features/AskMdt/availability'
import { ExplorerGrid } from './ExplorerGrid'
import { GroupByControl } from './GroupByControl'
import { SidebarExplorerReports } from './SidebarExplorerReports'
import { useExplorerDefinition } from '../hooks/useExplorerDefinition'
import * as explorerService from '../services/explorerService'
import { groupKeysOf, rowCountLabel } from '../utils/explorerFormat'
import type {
	ExplorerColumn,
	ExplorerGrainKey,
	ExplorerRow,
	ExplorerReport,
	ExplorerReportScope,
	ExplorerReportVisibility,
	ExplorerRowsMeta,
	ExplorerScopeType,
	ExplorerTemplateScope,
} from '../types/explorer.types'

/** The URL params that carry a template draft (MYD-598). */
const DRAFT_PARAMS = ['template', 'scopeType', 'scopeId'] as const

const DEFAULT_PER_PAGE = 50

/**
 * The grains a presentation story can be built from (MYD-506): a row is a
 * location or project, or an evaluation site standing for its location.
 * Selection is off for every other grain.
 */
const STORY_GRAINS: ReadonlyArray<string> = ['location', 'project', 'evaluationSite']

/** Where a bare or unrecognised Explorer URL lands. */
const DEFAULT_GRAIN_SLUG = 'locations'

/**
 * The Explorer screen (MYD-362).
 *
 * One component serves every grain; the URL decides which. Grain
 * parameterisation is structural from the start, so adding Projects or Tasks
 * later is a backend provider plus flipping `available`, not a new screen.
 *
 * ## Why rows live here and not in Redux
 *
 * Rows are per-screen and per-page, and they change on every filter keystroke.
 * Keeping them local means an unrelated subscriber never re-renders because
 * someone typed, and there is no stale page left in the store when a report is
 * edited. The slice holds only what a user carries between screens: grains, the
 * catalog and saved reports.
 */
export function ExplorerScreen() {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const { grain: grainSlug } = useParams<{ grain: string }>()
	// The URL carries a plural SLUG; the grain key is singular. Treating them as
	// the same made /explorer/locations resolve to nothing and redirect to
	// itself, rendering a blank page with no console error.
	const grainKey = grainForSlug(grainSlug)

	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [pickerOpen, setPickerOpen] = useState(false)
	const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())
	const [dialog, setDialog] = useState<{ open: boolean; mode: SaveReportMode }>({
		open: false,
		mode: 'create',
	})
	const [searchParams, setSearchParams] = useSearchParams()
	const [galleryOpen, setGalleryOpen] = useState(false)
	/** The scoped asset's name for the chip; null while looking it up or when unknown. */
	const [scopeName, setScopeName] = useState<string | null>(null)
	/** The name of the template the current draft came from, offered when saving it. */
	const [draftName, setDraftName] = useState('')

	const [rows, setRows] = useState<ExplorerRow[]>([])
	const [meta, setMeta] = useState<ExplorerRowsMeta | null>(null)
	const [page, setPage] = useState(0)
	const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE)
	const [rowsLoading, setRowsLoading] = useState(false)
	const [rowsError, setRowsError] = useState<string | null>(null)

	// Presentations from Explorer (MYD-506): selected row ids, and which dialog is open.
	const [selected, setSelected] = useState<Set<string>>(new Set())
	const [presentationDialog, setPresentationDialog] = useState<'stories' | 'table' | null>(null)
	const presentationsOn = isPresentationsEnabled()
	const selectable = presentationsOn && STORY_GRAINS.includes(grainKey ?? '')

	const { grains, grainsLoaded, columnsByGrain, reportsByGrain, isLoading, isSaving, error } =
		useSelector((state: any) => state.explorer)
	const reports: ExplorerReport[] = useMemo(
		() => reportsByGrain[grainKey ?? ''] ?? [],
		[reportsByGrain, grainKey],
	)

	// Grain-scoped: switching tabs empties the definition so nothing from the
	// previous grain is carried into the new one.
	const definition = useExplorerDefinition(grainKey)

	const grain = useMemo(
		() => grains.find((g: any) => g.key === grainKey) ?? null,
		[grains, grainKey],
	)
	const catalog: ExplorerColumn[] = useMemo(
		() => columnsByGrain[grainKey ?? ''] ?? [],
		[columnsByGrain, grainKey],
	)

	const byKey = useMemo(() => new Map(catalog.map(c => [c.key, c])), [catalog])
	const selectedColumns = useMemo(
		() =>
			definition.state.columns
				.map(key => byKey.get(key))
				.filter((c): c is ExplorerColumn => Boolean(c)),
		[definition.state.columns, byKey],
	)

	useEffect(() => {
		if (!grainsLoaded) dispatch(fetchGrains() as any)
	}, [dispatch, grainsLoaded])

	useEffect(() => {
		if (grain?.available && !columnsByGrain[grain.key]) {
			dispatch(fetchColumns(grain.key as ExplorerGrainKey) as any)
		}
	}, [dispatch, grain, columnsByGrain])

	useEffect(() => {
		if (grain?.available) dispatch(fetchReports(grain.key as ExplorerGrainKey) as any)
	}, [dispatch, grain])

	// Deep link: /explorer/:grain?report=<id> opens that report directly, so a
	// report can be shared as a URL rather than as "open Explorer and pick the
	// one called...".
	const reportIdParam = searchParams.get('report')
	useEffect(() => {
		if (!reportIdParam || reports.length === 0) return
		if (definition.loadedReport?.id === reportIdParam) return
		const target = reports.find(r => r.id === reportIdParam)
		if (target) definition.loadReport(target)
	}, [definition, reportIdParam, reports])

	// Start from the grain's own defaults so the first screen is a populated
	// grid rather than a blank one. An empty Explorer is the failure mode that
	// makes people close the tab and go back to Excel.
	const { startBlank } = definition
	const hasColumns = definition.state.columns.length > 0

	// A template draft (MYD-598): /explorer/:grain?template=<id>[&scopeType=&scopeId=].
	// Made concrete on the server WITHOUT saving (persist: false), so trying a
	// template leaves nothing behind. In the URL rather than in navigation
	// state, so a reload or a shared link reopens the same draft.
	const templateParam = searchParams.get('template')
	const scopeTypeParam = searchParams.get('scopeType') as ExplorerScopeType | null
	const scopeIdParam = searchParams.get('scopeId')
	// The grain is part of the key: when a draft redirects to its own grain's
	// tab, the definition there starts empty and the draft must load again.
	const draftKey = templateParam
		? `${grainKey}|${templateParam}|${scopeTypeParam ?? ''}|${scopeIdParam ?? ''}`
		: null
	const [loadedDraftKey, setLoadedDraftKey] = useState<string | null>(null)

	useEffect(() => {
		if (!grain?.available || !templateParam || !draftKey || draftKey === loadedDraftKey) return
		setLoadedDraftKey(draftKey)

		const scope = scopeTypeParam && scopeIdParam ? { type: scopeTypeParam, id: scopeIdParam } : null
		explorerService
			.instantiateTemplateDraft(templateParam, scope)
			.then(draft => {
				// A template of another grain belongs on that grain's tab. The
				// params travel with it, so the draft loads there instead.
				if (draft.grain !== grain.key) {
					navigate(`${routes.EXPLORER}/${slugForGrain(draft.grain)}?${searchParams.toString()}`, { replace: true })
					return
				}
				definition.loadDraft(draft.definition)
				setDraftName(draft.name)
			})
			.catch((err: any) => {
				// 403 on an asset they cannot open, 404 on a template they cannot
				// read: either way, say so and leave them on the plain grid.
				toast.error(err?.response?.data?.message || 'Could not open that template', { title: 'Template' })
				const next = new URLSearchParams(searchParams)
				DRAFT_PARAMS.forEach(param => next.delete(param))
				setSearchParams(next, { replace: true })
				startBlank(grain.defaultColumns, grain.defaultSort)
			})
	}, [grain, templateParam, scopeTypeParam, scopeIdParam, draftKey, loadedDraftKey, definition, navigate, searchParams, setSearchParams, startBlank])

	useEffect(() => {
		if (!grain?.available || catalog.length === 0) return
		if (hasColumns) return
		// A deep-linked report or template draft is about to load; seeding
		// defaults here would overwrite it and leave the URL pointing at
		// something the user is not looking at.
		if (reportIdParam || templateParam) return
		startBlank(grain.defaultColumns, grain.defaultSort)
	}, [grain, catalog.length, hasColumns, reportIdParam, templateParam, startBlank])

	const { columns, filters, sorts, groupBy, scope } = definition.state
	const scopeType = scope?.type
	const scopeId = scope?.id

	// Ask for the rows in plain words (MYD-628), where Ask MDT is offered.
	// Applied through the same setters as the hand controls, columns first: a
	// sort or grouping must name a selected column, and setColumns drops any
	// that does not.
	const userInfo = useSelector((state: any) => state.userLogin?.userInfo)
	const askAvailable = isAskMdtAvailable(userInfo)
	const { setColumns, setFilters, setSorts, setGroupBy } = definition
	const applyAsked = useCallback(
		(next: AskFilterLayout) => {
			setColumns(next.columns)
			setFilters(next.filters)
			setSorts(next.sorts)
			setGroupBy(next.groupBy)
		},
		[setColumns, setFilters, setSorts, setGroupBy],
	)

	// The chip names the asset. Looked up rather than carried, because a saved
	// report stores only the id. A failure leaves the generic "this project".
	useEffect(() => {
		setScopeName(null)
		if (!scopeType || !scopeId) return
		let cancelled = false
		explorerService
			.getScopeAsset(scopeType, scopeId)
			.then(asset => !cancelled && setScopeName(asset.name || null))
			.catch(() => !cancelled && setScopeName(null))
		return () => {
			cancelled = true
		}
	}, [scopeType, scopeId])

	// A selection made under other filters, or of another row type, no longer
	// describes what is on screen, so it does not outlive them.
	useEffect(() => {
		setSelected(new Set())
	}, [grainKey, filters])

	/** Run the current definition. */
	useEffect(() => {
		if (!grain?.available || columns.length === 0) {
			setRows([])
			setMeta(null)
			// A failure belonged to the definition that produced it. Leaving it up
			// while the grid is empty reads as a fresh failure of the new grain.
			setRowsError(null)
			return
		}

		let cancelled = false
		setRowsLoading(true)
		setRowsError(null)

		explorerService
			.previewRows({
				grain: grain.key as ExplorerGrainKey,
				columns,
				filters,
				sorts,
				groupBy,
				page: page + 1,
				perPage,
				scope: scopeType && scopeId ? { type: scopeType, id: scopeId } : null,
			})
			.then(result => {
				// A slower earlier request must not overwrite a newer result.
				if (cancelled) return
				setRows(result.data)
				setMeta(result.meta)
			})
			.catch((err: any) => {
				if (cancelled) return
				// The server names the offending column or explains why a report
				// is too expensive. That message is the whole value of the error.
				setRowsError(err?.response?.data?.message || 'Could not run this report')
				setRows([])
				setMeta(null)
			})
			.finally(() => {
				if (!cancelled) setRowsLoading(false)
			})

		return () => {
			cancelled = true
		}
	}, [grain, columns, filters, sorts, groupBy, page, perPage, scopeType, scopeId])

	// Any change to the shape of the report invalidates the page you are on.
	useEffect(() => {
		setPage(0)
	}, [columns, filters, sorts, groupBy, scopeType, scopeId])

	/**
	 * Keep the URL pointing at whatever report is actually open. Opening,
	 * saving or starting anything else also ends a template draft, so its
	 * params go too.
	 */
	const syncReportParam = useCallback(
		(id: string | null) => {
			const next = new URLSearchParams(searchParams)
			if (id) next.set('report', id)
			else next.delete('report')
			DRAFT_PARAMS.forEach(param => next.delete(param))
			setSearchParams(next, { replace: true })
			setDraftName('')
		},
		[searchParams, setSearchParams],
	)

	const openReport = useCallback(
		(report: ExplorerReport) => {
			definition.loadReport(report)
			syncReportParam(report.id)
		},
		[definition, syncReportParam],
	)

	const startNewReport = useCallback(() => {
		if (grain) definition.startBlank(grain.defaultColumns, grain.defaultSort)
		syncReportParam(null)
	}, [definition, grain, syncReportParam])

	/** A template was picked in the gallery: open it as a draft, on its own grain. */
	const openTemplate = useCallback(
		(template: ExplorerReport, templateScope: ExplorerReportScope | null) => {
			setGalleryOpen(false)
			navigate(templateDraftPath(routes.EXPLORER, template, templateScope))
		},
		[navigate],
	)

	/** Save over the loaded report. Only reachable when one is loaded and dirty. */
	const saveExisting = useCallback(async () => {
		const loaded = definition.loadedReport
		if (!loaded) return

		const result: any = await dispatch(
			updateReportThunk({ id: loaded.id, input: { definition: definition.toDefinition() } }) as any,
		)
		if (result.error) return

		// Re-baseline from the PERSISTED report, so an edit made while the save
		// was in flight stays flagged as dirty rather than being lost.
		definition.markSaved(result.payload)
		toast.success('Report saved')
	}, [definition, dispatch])

	const submitDialog = useCallback(
		async (name: string, visibility: ExplorerReportVisibility, templateScope?: ExplorerTemplateScope) => {
			const loaded = definition.loadedReport

			// The template is a copy of the SAVED report, so the report itself is
			// left open and unchanged (MYD-598).
			if (dialog.mode === 'saveAsTemplate' && loaded) {
				const result: any = await dispatch(
					saveReportAsTemplateThunk({ reportId: loaded.id, input: { name, visibility, templateScope } }) as any,
				)
				if (result.error) return
				setDialog({ open: false, mode: 'saveAsTemplate' })
				toast.success('Saved to your templates')
				return
			}

			if (dialog.mode === 'rename' && loaded) {
				const result: any = await dispatch(
					updateReportThunk({ id: loaded.id, input: { name, visibility } }) as any,
				)
				if (result.error) return
				definition.markSaved(result.payload)
				setDialog({ open: false, mode: 'rename' })
				toast.success('Report renamed')
				return
			}

			if (!grain) return
			const result: any = await dispatch(
				saveReportThunk({
					name,
					grain: grain.key as ExplorerGrainKey,
					visibility,
					definition: definition.toDefinition(),
				}) as any,
			)
			if (result.error) return

			definition.markSaved(result.payload)
			syncReportParam(result.payload.id)
			setDialog({ open: false, mode: 'create' })
			toast.success('Report saved')
		},
		[definition, dispatch, dialog.mode, grain, syncReportParam],
	)

	const removeReport = useCallback(async () => {
		const loaded = definition.loadedReport
		if (!loaded || !grain) return

		// toast.confirm, not window.confirm: the app replaced native dialogs
		// everywhere, and a native one would also block the render thread.
		const ok = await toast.confirm({
			title: 'Delete report',
			message: `Delete "${loaded.name}"? The layout is removed; no location data is affected.`,
		})
		if (!ok) return

		const result: any = await dispatch(
			deleteReportThunk({ id: loaded.id, grain: grain.key as ExplorerGrainKey }) as any,
		)
		if (result.error) return

		startNewReport()
		toast.success('Report deleted')
	}, [definition, dispatch, grain, startNewReport])

	// Group keys come from the SAME helper the grid groups with, so
	// "Collapse all" can never act on a set of keys the grid did not draw.
	const groupKeys = useMemo(() => groupKeysOf(rows, groupBy), [rows, groupBy])
	const allCollapsed = groupKeys.length > 0 && groupKeys.every(key => collapsedGroups.has(key))

	const toggleAll = useCallback(() => {
		setCollapsedGroups(prev => {
			// Every group already shut means the button reads "Expand all".
			const shutAlready = groupKeys.length > 0 && groupKeys.every(key => prev.has(key))
			return shutAlready ? new Set<string>() : new Set(groupKeys)
		})
	}, [groupKeys])

	// Regrouping invalidates the collapsed set: those keys belong to the old
	// grouping, and keeping them would collapse an unrelated group that happens
	// to share a value.
	useEffect(() => {
		setCollapsedGroups(new Set())
	}, [groupBy])

	const toggleGroup = useCallback((key: string) => {
		setCollapsedGroups(prev => {
			const next = new Set(prev)
			if (next.has(key)) next.delete(key)
			else next.add(key)
			return next
		})
	}, [])

	// An unknown grain in the URL sends the user somewhere real. The equality
	// guard matters: redirecting to the path we are already on renders nothing
	// at all, which is a blank screen with no error rather than a failure anyone
	// can act on.
	if (grainsLoaded && !grain && grainSlug !== DEFAULT_GRAIN_SLUG) {
		return <Navigate to={routes.EXPLORER_LOCATIONS} replace />
	}

	const rowLabel = grain?.rowLabel ?? 'row'
	const total = meta?.total ?? 0

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />
			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<GrainTabs
					grains={grains}
					activeKey={grainKey}
					onSelect={key => navigate(`${routes.EXPLORER}/${slugForGrain(key)}`)}
				/>

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						<div className="flex items-center justify-between mb-4">
							<div>
								<h1 className="text-2xl font-bold text-gray-900">Explorer</h1>
								<p className="text-sm text-gray-500">
									Build a report by choosing the columns you want.
								</p>
							</div>
							<div className="flex items-center gap-3 flex-wrap justify-end">
								{meta && (
									<span className="text-xs text-gray-500">{rowCountLabel(total, rowLabel)}</span>
								)}
								{grain?.available && (
									<SavedReportPicker
										reports={reports}
										loadedReport={definition.loadedReport}
										dirty={definition.dirty}
										hasColumns={columns.length > 0}
										isSaving={isSaving}
										onOpen={openReport}
										onNew={startNewReport}
										onSave={definition.loadedReport ? saveExisting : () => setDialog({ open: true, mode: 'create' })}
										onSaveAs={() => setDialog({ open: true, mode: 'saveAs' })}
										onRename={() => setDialog({ open: true, mode: 'rename' })}
										onDelete={removeReport}
										onSaveAsTemplate={() => setDialog({ open: true, mode: 'saveAsTemplate' })}
									/>
								)}
								{grain?.available && (
									<Button variant="ghost" isOutline onClick={() => setGalleryOpen(true)}>
										<LayoutTemplate className="w-4 h-4 mr-2" />
										New from template
									</Button>
								)}
								<Button variant="ghost" isOutline onClick={() => setPickerOpen(true)}>
									<Columns3 className="w-4 h-4 mr-2" />
									Columns
									{columns.length > 0 && (
										<span className="ml-1.5 text-xs text-gray-500">({columns.length})</span>
									)}
								</Button>
								{grain?.available && presentationsOn && (
									<Button variant="ghost" isOutline onClick={() => setPresentationDialog('table')} disabled={columns.length === 0}>
										<PresentationIcon className="w-4 h-4 mr-2" />
										Add to presentation
									</Button>
								)}
								{grain?.available && (
									<ExportMenu
										grain={grain.key as ExplorerGrainKey}
										// The CURRENT definition, including unsaved edits: the user
										// is asking for what is on screen, not for the last version
										// they happened to save.
										definition={definition.toDefinition()}
										reportName={definition.loadedReport?.name || grain.label}
										disabled={columns.length === 0}
									/>
								)}
							</div>
						</div>

						{grain && !grain.available ? (
							<Alert>
								<AlertDescription>
									{grain.label} reports are not available yet.
								</AlertDescription>
							</Alert>
						) : (
							<>
								{/* Alert's variants here are primary | success | danger | warning |
								    info. There is no "destructive" — this is not the stock ShadCN
								    component. */}
								{(rowsError || error) && (
									<Alert variant="danger" className="mb-4">
										<AlertDescription>{rowsError || error}</AlertDescription>
									</Alert>
								)}

								{scopeType && scopeId && grain && (
									<ScopeChip
										className="mb-4"
										grainLabel={GRAIN_LABELS[grain.key] ?? grain.label}
										assetName={scopeName}
										assetType={scopeType}
										onClear={() => definition.setScope(null)}
									/>
								)}

								{grain && askAvailable && (
									<AskFilterInput
										key={grain.key}
										grain={grain.key as ExplorerGrainKey}
										layout={{ columns, filters, sorts, groupBy, scope }}
										onApply={applyAsked}
									/>
								)}

								{grain && (
									<div className="flex items-start justify-between gap-4 flex-wrap">
										<ExplorerScopeContext.Provider value={scopeType && scopeId ? { type: scopeType, id: scopeId } : null}>
											<FilterBar
												catalog={catalog}
												filters={filters}
												grain={grain.key as ExplorerGrainKey}
												onChange={definition.setFilters}
											/>
										</ExplorerScopeContext.Provider>
										<div className="mb-4">
											<GroupByControl
												columns={selectedColumns}
												groupBy={groupBy}
												onChange={definition.setGroupBy}
												groupCount={groupKeys.length}
												allCollapsed={allCollapsed}
												onToggleAll={toggleAll}
											/>
										</div>
									</div>
								)}

								{selectable && selected.size > 0 && (
									<div className="mb-3 flex items-center gap-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm" role="toolbar" aria-label="Selection">
										<span className="font-medium text-gray-900">{selected.size} selected</span>
										<button type="button" onClick={() => setSelected(new Set())} className="inline-flex items-center gap-1 text-xs text-gray-600 hover:underline">
											<X className="h-3.5 w-3.5" /> Clear
										</button>
										<Button className="ml-auto" onClick={() => setPresentationDialog('stories')}>
											<PresentationIcon className="w-4 h-4 mr-2" />
											Build stories
										</Button>
									</div>
								)}

								<ExplorerGrid
									selection={selectable ? { selected, onChange: setSelected } : undefined}
									columns={selectedColumns}
									rows={rows}
									sorts={sorts}
									groupBy={groupBy}
									widths={definition.state.widths}
									onResizeColumn={definition.setColumnWidth}
									collapsedGroups={collapsedGroups}
									onToggleGroup={toggleGroup}
									onToggleSort={definition.toggleSort}
									isLoading={rowsLoading && rows.length === 0}
									isFiltered={filters.length > 0}
									rowLabel={rowLabel}
									onPickColumns={() => setPickerOpen(true)}
									onClearFilters={() => definition.setFilters([])}
								/>

								{meta && total > perPage && (
									<div className="mt-3">
										<TablePagination
											page={page}
											rowsPerPage={perPage}
											count={total}
											onPageChange={setPage}
											onRowsPerPageChange={setPerPage}
										/>
									</div>
								)}
							</>
						)}
					</main>

					<RightPanel storageKey="explorer" label="Explorer panel">
						<div className="flex flex-col h-1/2 border-b border-gray-200 overflow-y-auto">
							<SidebarExplorerReports
								reports={reports}
								loadedReportId={definition.loadedReport?.id ?? null}
								isLoading={isLoading}
								onOpen={openReport}
							/>
						</div>
						{/* Vendor Partnerships card, bottom half */}
						<div className="flex flex-col h-1/2">
							<div className="px-4 py-3 border-b border-gray-100">
								<span className="flex items-center gap-2 text-gray-600 text-base font-semibold hover:scale-105 transition-transform duration-150">
									<Handshake className="w-4 h-4" /> Vendor Partnerships
								</span>
							</div>
							<div className="flex-1 overflow-y-auto">
								<VendorList />
							</div>
						</div>
					</RightPanel>
				</div>
			</div>

			{selectable && (
				<BuildStoriesDialog open={presentationDialog === 'stories'} onClose={() => setPresentationDialog(null)} grain={grainKey as StoryGrain} ids={[...selected]} />
			)}
			{presentationsOn && grain && (
				<AddToPresentationDialog
					open={presentationDialog === 'table'}
					onClose={() => setPresentationDialog(null)}
					title={definition.loadedReport?.name || grain.label}
					// A saved report with nothing unsaved goes by its id, so editing it
					// in Explorer later updates the slide. Otherwise, what is on screen.
					linkedToReport={Boolean(definition.loadedReport && !definition.dirty)}
					tableConfig={
						definition.loadedReport && !definition.dirty
							? { savedReportId: definition.loadedReport.id }
							: { grain: grain.key, columns, filters: filters.map(toStoredFilter), sorts, ...(groupBy ? { groupBy } : {}) }
					}
				/>
			)}

			<SaveReportDialog
				open={dialog.open}
				mode={dialog.mode}
				// Rename pre-fills the current name; save-as suggests a copy so the
				// user is not offered a name that already exists on another report.
				initialName={
					dialog.mode === 'rename' || dialog.mode === 'saveAsTemplate'
						? (definition.loadedReport?.name ?? '')
						: dialog.mode === 'saveAs'
							? `${definition.loadedReport?.name ?? ''} copy`
							: // A template draft offers the template's own name.
								draftName
				}
				initialVisibility={definition.loadedReport?.visibility ?? 'personal'}
				grain={grain?.key ?? ''}
				// A report narrowed to an asset becomes a template used on that
				// kind of asset; an unscoped one, on all of the grain's rows.
				initialTemplateScope={
					(scopeType as ExplorerTemplateScope | undefined) ??
					(grain ? HOME_TEMPLATE_SCOPE_BY_GRAIN[grain.key] : null)
				}
				isSaving={isSaving}
				error={error}
				onClose={() => {
					setDialog(prev => ({ ...prev, open: false }))
					if (error) dispatch(clearError())
				}}
				onSubmit={submitDialog}
			/>

			<ExplorerTemplateGallery
				open={galleryOpen}
				onClose={() => setGalleryOpen(false)}
				initialTab={grain ? HOME_TEMPLATE_SCOPE_BY_GRAIN[grain.key] : 'project'}
				onUse={openTemplate}
			/>

			<ColumnPickerSheet
				open={pickerOpen}
				onClose={() => {
					setPickerOpen(false)
					if (error) dispatch(clearError())
				}}
				catalog={catalog}
				selected={columns}
				onChange={definition.setColumns}
			/>
		</div>
	)
}

/**
 * The grain tabs.
 *
 * Built here rather than with `SecondaryNavCN` because that component renders
 * every entry as a `NavLink` and has no disabled state. A not-yet-available
 * grain has to read as a promise, not as a link that lands on an apology, so
 * it renders as a disabled tab in the same visual language.
 */
function GrainTabs({
	grains,
	activeKey,
	onSelect,
}: {
	grains: any[]
	activeKey: string | null
	onSelect: (key: string) => void
}) {
	if (grains.length === 0) return null

	return (
		<div className="flex-shrink-0 border-b border-gray-200 bg-white">
			<nav className="flex items-center gap-1 h-10 px-6 overflow-x-auto">
				{grains.map(grain => {
					const isActive = grain.key === activeKey
					return (
						<button
							key={grain.key}
							type="button"
							disabled={!grain.available}
							onClick={() => onSelect(grain.key)}
							title={grain.available ? undefined : `${grain.label} reports are coming soon`}
							className={cn(
								'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full transition-colors whitespace-nowrap',
								isActive
									? 'bg-[#0080fe]/10 text-[#0080fe] font-medium'
									: grain.available
										? 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
										: 'text-gray-300 cursor-not-allowed',
							)}
						>
							{grain.label}
						</button>
					)
				})}
			</nav>
		</div>
	)
}

export default ExplorerScreen
